import React from 'react';
import { XIcons } from '../primitives.jsx';
import { XEnv } from '../data.jsx';
import { XApi } from '../services.js';
import { XShell } from '../shell.jsx';
import { useT } from '../i18n.jsx';
import { Footer } from './market.jsx';
import { isFullAccountSession } from '../api/ephemeral-credentials.js';
import {
  buildSchemaInput,
  dnssecPresentation,
  dnsRecordSupportsProxy,
  displayDomainName,
  formatExpiry,
  hasCloudflareDnsCapabilities,
  hasMixedAddressProxyState,
  hasNsDelegationShadowRisk,
  isCloudflareDomain,
  isDnsRecordManaged,
  isDnsRecordReadOnly,
  isDnsRecordTypeWritable,
  isDomainCapability,
  makeIdempotencyKey,
  normalizeDnsCapabilities,
  normalizeDnsRecords,
  normalizeDomainList,
  schemaDraft,
  unwrapActionResponse,
  validateDnsRecordInput,
  validateDnsRecordSetInput,
} from './domain-console-model.js';

const COPY = {
  en: {
    eyebrow: 'xAPI Domains', title: 'A quiet place for every domain.',
    subtitle: 'Manage Cloudflare domains and authoritative DNS through the same xAPI identity.',
    daily: 'Domain manager', api: 'API details',
    mine: 'My domains', refresh: 'Refresh', noDomains: 'No domains here yet.',
    noDomainsHint: 'Cloudflare domains registered through the API will appear here.',
    needsKey: 'Sign in to load and manage your domains.', identity: 'xdomain uses a short-lived session token tied to your xAPI account.', setKey: 'Set XAPI_KEY',
    login: 'Sign in', status: 'Status', expires: 'Expires', registrar: 'Registrar', manage: 'Manage',
    dns: 'DNS records', close: 'Close', noDns: 'No DNS records yet.', loadingDns: 'Loading DNS…',
    loading: 'Loading domains…', addRecord: 'Add record', editRecord: 'Edit record',
    createRecord: 'Create record', saveRecord: 'Save changes', savingRecord: 'Saving…', cancel: 'Cancel',
    deleteRecord: 'Delete', confirmDelete: 'Delete this DNS record?', deletingRecord: 'Deleting…',
    type: 'Type', name: 'Name', value: 'Value', target: 'Target', priority: 'Priority', weight: 'Weight', port: 'Port', flags: 'Flags', tag: 'Tag', actions: 'Actions',
    priorityHint: 'Required for MX and SRV records.', dnsMutationSuccess: 'DNS records updated.',
    ttl: 'TTL', ttlAutomatic: '1 means automatic; otherwise use the Cloudflare range shown below.',
    proxy: 'Proxy', proxied: 'Proxied', dnsOnly: 'DNS only', proxyHint: 'Available for A, AAAA and CNAME records on Cloudflare.', proxyGroupHint: 'Cloudflare treats every A and AAAA record with this name as proxied when any one is proxied.',
    proxyUnavailable: 'Unavailable', proxyUnavailableHint: 'Cloudflare marks this record as non-proxiable.', recordLocked: 'Locked by Cloudflare', recordManaged: 'Managed by Cloudflare', recordShadowed: 'Shadowed by {count} record(s)',
    capabilitiesUnavailable: 'DNS controls stay disabled until Cloudflare capabilities load successfully.',
    dnsRequired: 'Type, name and value are required.', dnsTypeUnsupported: 'This record type is not supported by Cloudflare DNS.',
    dnsInvalidName: 'Enter @ or a valid relative DNS name.',
    dnsInvalidIpv4: 'Enter a valid IPv4 address.', dnsInvalidIpv6: 'Enter a valid IPv6 address.',
    dnsInvalidHostname: 'Enter a valid hostname.',
    dnsInvalidPriority: 'Priority must be an integer from 0 to 65535.', dnsInvalidTtl: 'TTL is outside the Cloudflare-supported range.',
    dnsProxyUnsupported: 'Proxy status is unavailable for this Cloudflare record type.', dnsProxyTtl: 'Proxied records must use automatic TTL.',
    dnsInvalidSrv: 'Priority, weight and port must be integers from 0 to 65535.', dnsInvalidCaa: 'CAA flags must be 0–255 and a tag is required.', dnsValueTooLong: 'A DNS value cannot exceed 4096 bytes.', dnsTxtSetTooLong: 'TXT records with this name cannot exceed 8192 combined characters.', dnsNsLimit: 'A delegation can contain at most 10 NS records.', dnsNsShadowConfirm: 'This NS delegation will hide existing records at or below this name. Continue?',
    comment: 'Comment', tags: 'Tags', commentHint: 'Up to 100 characters.', tagsHint: 'Comma-separated name:value tags; availability depends on the Cloudflare plan.', dnsCommentTooLong: 'Comments can contain at most 100 characters.', dnsInvalidTags: 'Use at most 20 name:value tags; names may contain letters, numbers, - and _.',
    filterDns: 'Search name or value', allTypes: 'All types', unsupportedType: 'Editing unavailable', recordsShown: '{shown} of {total}',
    dnssec: 'DNSSEC', dnssecActive: 'Protected', dnssecPending: 'Enabling', dnssecDisabled: 'Disabled', dnssecPendingDisabled: 'Disabling', dnssecError: 'Error', dnssecEnable: 'Enable DNSSEC', dnssecDisable: 'Disable DNSSEC', dnssecBusy: 'Updating…', dnssecHint: 'Cloudflare signs the zone and manages the DS record for Registrar domains.', dnssecDisableConfirm: 'Disable DNSSEC for this domain?', dnssecUnavailable: 'DNSSEC status unavailable',
    apiLead: 'Loaded from the capability backend. Select an action, complete its generated form, and run it here without leaving xdomain.',
    request: 'Request', response: 'Response', schema: 'JSON Schema', run: 'Run capability', running: 'Running…',
    apiLoading: 'Loading xdomain capabilities…', apiEmpty: 'No domain capabilities were returned.',
    apiError: 'Could not load capability definitions.', apiKeyRequired: 'Set XAPI_KEY to run this capability.',
    noResponse: 'Run the capability to inspect its response.', optional: 'optional',
    demo: 'Demo data', previous: 'Previous', next: 'Next',
  },
  zh: {
    eyebrow: 'xAPI 域名', title: '让每个域名都有一个安静的管理空间。',
    subtitle: '使用同一个 xAPI 身份管理 Cloudflare 域名及其权威 DNS。',
    daily: '域名管理', api: '接口详情',
    mine: '我的域名', refresh: '刷新', noDomains: '这里还没有域名。',
    noDomainsHint: '通过 API 注册的 Cloudflare 域名会显示在这里。',
    needsKey: '登录后即可加载和管理你的域名。', identity: 'xdomain 会使用绑定当前 xAPI 账户的短期会话令牌。', setKey: '设置 XAPI_KEY',
    login: '登录', status: '状态', expires: '到期时间', registrar: '注册商', manage: '管理',
    dns: 'DNS 记录', close: '关闭', noDns: '还没有 DNS 记录。', loadingDns: '正在加载 DNS…',
    loading: '正在加载域名…', addRecord: '添加记录', editRecord: '编辑记录',
    createRecord: '创建记录', saveRecord: '保存修改', savingRecord: '正在保存…', cancel: '取消',
    deleteRecord: '删除', confirmDelete: '确认删除这条 DNS 记录？', deletingRecord: '正在删除…',
    type: '类型', name: '名称', value: '记录值', target: '目标主机', priority: '优先级', weight: '权重', port: '端口', flags: '标志', tag: '标签', actions: '操作',
    priorityHint: 'MX 和 SRV 记录必须填写。', dnsMutationSuccess: 'DNS 记录已更新。',
    ttl: 'TTL', ttlAutomatic: '1 表示自动；其他值需符合下方 Cloudflare 范围。',
    proxy: '代理状态', proxied: '已代理', dnsOnly: '仅 DNS', proxyHint: 'Cloudflare 的 A、AAAA 和 CNAME 记录可用。', proxyGroupHint: '同名 A/AAAA 记录中只要有一条开启代理，Cloudflare 就会将这一组地址记录都视为已代理。',
    proxyUnavailable: '不可代理', proxyUnavailableHint: 'Cloudflare 将这条记录标记为不可代理。', recordLocked: 'Cloudflare 已锁定', recordManaged: '由 Cloudflare 管理', recordShadowed: '被 {count} 条记录遮蔽',
    capabilitiesUnavailable: 'Cloudflare capability 成功加载前，DNS 写入功能保持禁用。',
    dnsRequired: '类型、名称和记录值均为必填项。', dnsTypeUnsupported: 'Cloudflare DNS 不支持这种记录类型。',
    dnsInvalidName: '请输入 @ 或有效的相对 DNS 名称。',
    dnsInvalidIpv4: '请输入有效的 IPv4 地址。', dnsInvalidIpv6: '请输入有效的 IPv6 地址。',
    dnsInvalidHostname: '请输入有效的主机名。',
    dnsInvalidPriority: '优先级必须是 0 到 65535 的整数。', dnsInvalidTtl: 'TTL 超出 Cloudflare 支持的范围。',
    dnsProxyUnsupported: '该 Cloudflare 记录类型不支持代理。', dnsProxyTtl: '开启代理时必须使用自动 TTL。',
    dnsInvalidSrv: '优先级、权重和端口必须是 0 到 65535 的整数。', dnsInvalidCaa: 'CAA 标志必须是 0 到 255 的整数，且必须填写标签。', dnsValueTooLong: '单条 DNS 记录值不能超过 4096 字节。', dnsTxtSetTooLong: '同名 TXT 记录的内容合计不能超过 8192 个字符。', dnsNsLimit: '同一个委派名称最多只能配置 10 条 NS 记录。', dnsNsShadowConfirm: '这个 NS 委派会隐藏该名称及其下级名称的现有记录，确认继续吗？',
    comment: '备注', tags: '记录标签', commentHint: '最多 100 个字符。', tagsHint: '使用逗号分隔 name:value 标签；是否可用取决于 Cloudflare 套餐。', dnsCommentTooLong: '备注最多包含 100 个字符。', dnsInvalidTags: '最多填写 20 个 name:value 标签；名称仅可包含字母、数字、- 和 _。',
    filterDns: '搜索名称或记录值', allTypes: '全部类型', unsupportedType: '不支持编辑', recordsShown: '显示 {shown} / {total}',
    dnssec: 'DNSSEC', dnssecActive: '已保护', dnssecPending: '启用中', dnssecDisabled: '未启用', dnssecPendingDisabled: '停用中', dnssecError: '错误', dnssecEnable: '启用 DNSSEC', dnssecDisable: '停用 DNSSEC', dnssecBusy: '正在更新…', dnssecHint: 'Cloudflare 会为该区域签名，并为 Registrar 域名管理 DS 记录。', dnssecDisableConfirm: '确认停用该域名的 DNSSEC？', dnssecUnavailable: '无法读取 DNSSEC 状态',
    apiLead: '接口定义来自 capability 后端。选择接口、填写自动生成的表单并直接调用，无需离开 xdomain 页面。',
    request: '请求', response: '响应', schema: 'JSON Schema', run: '运行 Capability', running: '运行中…',
    apiLoading: '正在加载 xdomain capability…', apiEmpty: '后端没有返回域名 capability。',
    apiError: '无法加载 capability 定义。', apiKeyRequired: '设置 XAPI_KEY 后才能运行此接口。',
    noResponse: '运行接口后可在这里查看响应。', optional: '可选',
    demo: '演示数据', previous: '上一页', next: '下一页',
  },
};

const DEMO_DOMAINS = [
  { id: 'demo-domain-1', domain: 'northstar.ai', status: 'active', registrar: 'Cloudflare', expires_at: '2027-08-18T00:00:00.000Z' },
  { id: 'demo-domain-2', domain: 'quietorbit.dev', status: 'active', registrar: 'Cloudflare', expires_at: '2027-11-03T00:00:00.000Z' },
  { id: 'demo-domain-3', domain: 'xapi.tools', status: 'pending', registrar: 'Cloudflare', expires_at: '2027-09-26T00:00:00.000Z' },
];

const DOMAIN_PAGE_SIZE = 50;
const DNS_PAGE_SIZE = 20;

const DEMO_DNS = {
  'demo-domain-1': [
    { id: 'dns-1', type: 'A', name: '@', value: '192.0.2.42', ttl: 1, proxied: true },
    { id: 'dns-2', type: 'CNAME', name: 'www', value: 'northstar.ai', ttl: 1, proxied: true },
    { id: 'dns-3', type: 'TXT', name: '@', value: 'v=spf1 include:_spf.example.net ~all', ttl: 3600, proxied: false, locked: true, managed_by_apps: true, comment: 'Managed by Cloudflare Email Routing' },
    { id: 'dns-7', type: 'CAA', name: '@', value: 'letsencrypt.org', flags: '0', tag: 'issue', ttl: 3600, comment: 'Certificate authority' },
    { id: 'dns-8', type: 'SRV', name: '_sip._tcp', value: 'sip.example.net', priority: '10', weight: '5', port: '5060', ttl: 3600 },
  ],
  'demo-domain-2': [
    { id: 'dns-4', type: 'CNAME', name: 'www', value: 'quiet-orbit.pages.dev', ttl: 1, proxied: false, proxiable: false, managed_by_argo_tunnel: true, shadowed_by: ['dns-shadow-1'] },
    { id: 'dns-5', type: 'MX', name: '@', value: 'mail.example.net', ttl: 3600, priority: '10', proxied: false },
  ],
  'demo-domain-3': [
    { id: 'dns-6', type: 'A', name: '@', value: '198.51.100.18', ttl: 1, proxied: true },
  ],
};

const statusTone = (status) => {
  const value = String(status || '').toLowerCase();
  if (value === 'active' || value === 'succeeded') return 'ok';
  if (value.includes('failed') || value === 'expired') return 'bad';
  return 'pending';
};

const action = async (actionId, input, signal, apiKey) => {
  const response = await XApi.actionsService.execute({ actionId, input, signal, apiKey });
  return unwrapActionResponse(response);
};

const KeyGate = ({ copy, authed, navigate }) => (
  <div className="xd-key-gate card">
    <div className="xd-orbit"><XIcons.IconKey size={20}/></div>
    <div>
      <h3>{copy.needsKey}</h3>
      <p>{copy.identity}</p>
    </div>
    <button className="btn btn-primary" onClick={() => navigate(authed ? 'console' : 'login', authed ? { tab: 'env', focus: 'XAPI_KEY' } : {})}>
      {authed ? copy.setKey : copy.login}
    </button>
  </div>
);

const emptyDnsForm = (capabilities) => ({
  type: capabilities.record_types[0] || 'A',
  subdomain: '@',
  value: '',
  priority: '',
  weight: '', port: '', flags: '0', tag: 'issue', comment: '', tags: '',
  ttl: String(capabilities.ttl.default),
  proxied: false,
});

const dnsRecordForm = (record, capabilities) => ({
  type: String(record?.type || 'A').toUpperCase(),
  subdomain: record?.subdomain ?? record?.name ?? '@',
  value: record?.value ?? record?.content ?? '',
  priority: record?.priority ?? '',
  weight: record?.weight ?? '',
  port: record?.port ?? '',
  flags: record?.flags ?? '0',
  tag: record?.tag ?? 'issue',
  comment: record?.comment ?? '',
  tags: Array.isArray(record?.tags) ? record.tags.join(', ') : '',
  ttl: String(record?.ttl ?? capabilities.ttl.default),
  proxied: !!record?.proxied,
});

const dnsRecordReference = (record, index) => {
  const id = record?.record_id ?? record?.id;
  return id == null || id === '' ? { record_index: index } : { record_id: String(id) };
};

const DomainDetail = ({ domain, copy, locale, onClose, demo = false, runAction = action }) => {
  const initialCapabilities = React.useMemo(() => normalizeDnsCapabilities({}), []);
  const [records, setRecords] = React.useState([]);
  const [capabilities, setCapabilities] = React.useState(initialCapabilities);
  const [capabilitiesReady, setCapabilitiesReady] = React.useState(demo);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');
  const [editor, setEditor] = React.useState(null);
  const [form, setForm] = React.useState(() => emptyDnsForm(initialCapabilities));
  const [saving, setSaving] = React.useState(false);
  const [deletingIndex, setDeletingIndex] = React.useState(null);
  const [confirmIndex, setConfirmIndex] = React.useState(null);
  const [recordQuery, setRecordQuery] = React.useState('');
  const [recordType, setRecordType] = React.useState('');
  const [recordPage, setRecordPage] = React.useState(0);
  const [dnssec, setDnssec] = React.useState(null);
  const [dnssecBusy, setDnssecBusy] = React.useState(false);
  const editingRecord = editor?.mode === 'edit' ? records[editor.index] : null;

  const loadRecords = React.useCallback(async () => {
    setLoading(true); setError('');
    if (demo) {
      setRecords((DEMO_DNS[domain.id] || []).map((record) => ({ ...record })));
      setCapabilities(normalizeDnsCapabilities({}));
      setCapabilitiesReady(true);
      setDnssec({ status: 'active', algorithm: '13', digest_algorithm: 'SHA256', key_tag: 2371 });
    } else {
      setCapabilitiesReady(false);
      setEditor(null);
      try {
        const [value, dnssecValue] = await Promise.all([
          runAction('dns.list', { domain_id: domain.id }),
          runAction('dns.dnssec.get', { domain_id: domain.id }).catch(() => null),
        ]);
        if (!hasCloudflareDnsCapabilities(value)) throw new Error(copy.capabilitiesUnavailable);
        setRecords(normalizeDnsRecords(value));
        setCapabilities(normalizeDnsCapabilities(value));
        setCapabilitiesReady(true);
        setDnssec(dnssecValue);
      }
      catch (e) { setError(e.message || 'Could not load DNS records'); }
    }
    setLoading(false);
  }, [copy.capabilitiesUnavailable, domain.id, demo, runAction]);

  React.useEffect(() => { loadRecords(); }, [loadRecords]);

  const beginCreate = () => {
    if (!capabilitiesReady) { setError(copy.capabilitiesUnavailable); return; }
    setEditor({ mode: 'create', index: -1 }); setForm(emptyDnsForm(capabilities)); setError(''); setNotice('');
  };
  const beginEdit = (record, index) => {
    if (!capabilitiesReady) { setError(copy.capabilitiesUnavailable); return; }
    if (isDnsRecordReadOnly(record) || !isDnsRecordTypeWritable(record, capabilities)) return;
    setEditor({ mode: 'edit', index }); setForm(dnsRecordForm(record, capabilities)); setError(''); setNotice('');
  };
  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const setType = (type) => setForm((current) => {
    const nextType = type.toUpperCase();
    const proxyAvailable = dnsRecordSupportsProxy(editingRecord, nextType, capabilities);
    const priorityRelevant = nextType === 'MX' || nextType === 'SRV';
    return {
      ...current,
      type: nextType,
      proxied: proxyAvailable ? current.proxied : false,
      priority: priorityRelevant ? (current.priority || '10') : '',
      weight: nextType === 'SRV' ? (current.weight || '0') : '',
      port: nextType === 'SRV' ? (current.port || '') : '',
      flags: nextType === 'CAA' ? (current.flags || '0') : '',
      tag: nextType === 'CAA' ? (current.tag || 'issue') : '',
    };
  });

  const saveRecord = async (event) => {
    event.preventDefault(); setError(''); setNotice('');
    if (!capabilitiesReady) { setError(copy.capabilitiesUnavailable); return; }
    if (editingRecord && isDnsRecordReadOnly(editingRecord)) return;
    const validation = validateDnsRecordInput(form, capabilities);
    if (validation.error) { setError(copy[validation.error] || validation.error); return; }
    const setValidation = validateDnsRecordSetInput(
      form,
      records,
      editor.mode === 'edit' ? editor.index : -1,
    );
    if (setValidation.error) { setError(copy[setValidation.error] || setValidation.error); return; }
    const acknowledgeShadowing = hasNsDelegationShadowRisk(
      form,
      records,
      editor.mode === 'edit' ? editor.index : -1,
    );
    if (acknowledgeShadowing && !window.confirm(copy.dnsNsShadowConfirm)) return;
    setSaving(true);
    const type = form.type.trim().toUpperCase();
    const proxyAvailable = dnsRecordSupportsProxy(editingRecord, type, capabilities);
    const tags = form.tags.split(',').map((tag) => tag.trim()).filter(Boolean);
    const payload = {
      domain_id: domain.id,
      idempotency_key: makeIdempotencyKey('xdomain-dns'),
      subdomain: form.subdomain.trim(),
      type,
      value: form.value.trim(),
      ...(form.priority.trim() ? { priority: form.priority.trim() } : {}),
      ...(form.weight.trim() ? { weight: form.weight.trim() } : {}),
      ...(form.port.trim() ? { port: form.port.trim() } : {}),
      ...(form.flags.trim() ? { flags: form.flags.trim() } : {}),
      ...(form.tag.trim() ? { tag: form.tag.trim() } : {}),
      ...(form.comment.trim() || editingRecord?.comment !== undefined ? { comment: form.comment.trim() } : {}),
      ...(tags.length || editingRecord?.tags !== undefined ? { tags } : {}),
      ...(form.ttl !== '' ? { ttl: Number(form.ttl) } : {}),
      ...(proxyAvailable ? { proxied: !!form.proxied } : {}),
      ...(editingRecord?.modified_on ? { record_modified_on: editingRecord.modified_on } : {}),
      ...(acknowledgeShadowing ? { acknowledge_shadowing: true } : {}),
    };
    try {
      if (editor.mode === 'edit') Object.assign(payload, dnsRecordReference(records[editor.index], editor.index));
      if (demo) {
        setRecords((current) => editor.mode === 'edit'
          ? current.map((record, index) => index === editor.index ? { ...record, ...payload, name: payload.subdomain } : record)
          : [...current, { id: `demo-dns-${Date.now()}`, ...payload, name: payload.subdomain }]);
      } else {
        await runAction('dns.upsert', payload);
        await loadRecords();
      }
      setEditor(null); setNotice(copy.dnsMutationSuccess);
    } catch (e) { setError(e.message || 'Could not save DNS record'); }
    finally { setSaving(false); }
  };

  const proxyAvailable = dnsRecordSupportsProxy(editingRecord, form.type, capabilities);
  const proxyTypeSupported = capabilities.proxied.supported && capabilities.proxied.record_types.includes(form.type);
  const priorityRelevant = form.type === 'MX' || form.type === 'SRV';
  const srvRelevant = form.type === 'SRV';
  const caaRelevant = form.type === 'CAA';
  const ttlRange = capabilities.ttl.automatic === undefined
    ? `${capabilities.ttl.min}–${capabilities.ttl.max}`
    : `${capabilities.ttl.automatic} / ${capabilities.ttl.min}–${capabilities.ttl.max}`;
  const filteredRecordEntries = records.map((record, index) => ({ record, index })).filter(({ record }) => {
    const type = String(record.type || '').toUpperCase();
    const query = recordQuery.trim().toLowerCase();
    return (!recordType || type === recordType) && (!query || `${record.name ?? record.subdomain ?? ''} ${record.value ?? record.content ?? ''}`.toLowerCase().includes(query));
  });
  const recordPageCount = Math.max(1, Math.ceil(filteredRecordEntries.length / DNS_PAGE_SIZE));
  const safeRecordPage = Math.min(recordPage, recordPageCount - 1);
  const visibleRecordEntries = filteredRecordEntries.slice(safeRecordPage * DNS_PAGE_SIZE, (safeRecordPage + 1) * DNS_PAGE_SIZE);

  const deleteRecord = async (record, index) => {
    if (!capabilitiesReady) { setError(copy.capabilitiesUnavailable); return; }
    if (isDnsRecordReadOnly(record)) return;
    setDeletingIndex(index); setError(''); setNotice('');
    try {
      if (demo) setRecords((current) => current.filter((_, itemIndex) => itemIndex !== index));
      else {
        await runAction('dns.delete', {
          domain_id: domain.id,
          idempotency_key: makeIdempotencyKey('xdomain-dns-delete'),
          ...dnsRecordReference(record, index),
          ...(record.modified_on ? { record_modified_on: record.modified_on } : {}),
        });
        await loadRecords();
      }
      setConfirmIndex(null); setNotice(copy.dnsMutationSuccess);
    } catch (e) { setError(e.message || 'Could not delete DNS record'); }
    finally { setDeletingIndex(null); }
  };

  const changeDnssec = async (enabled) => {
    if (!enabled && !window.confirm(copy.dnssecDisableConfirm)) return;
    setDnssecBusy(true); setError(''); setNotice('');
    try {
      const next = demo
        ? { ...(dnssec || {}), status: enabled ? 'active' : 'disabled' }
        : await runAction('dns.dnssec.set', { domain_id: domain.id, enabled, idempotency_key: makeIdempotencyKey('xdomain-dnssec') });
      setDnssec(next);
    } catch (e) { setError(e.message || copy.dnssecUnavailable); }
    finally { setDnssecBusy(false); }
  };

  const dnssecState = dnssecPresentation(dnssec);
  React.useEffect(() => {
    if (demo || !dnssecState.transitional) return undefined;
    let cancelled = false;
    const timer = window.setInterval(() => {
      runAction('dns.dnssec.get', { domain_id: domain.id })
        .then((next) => { if (!cancelled) setDnssec(next); })
        .catch(() => undefined);
    }, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [demo, dnssecState.transitional, domain.id, runAction]);
  const proxyGroupWarning = hasMixedAddressProxyState(
    form,
    records,
    editor?.mode === 'edit' ? editor.index : -1,
  );

  return (
    <div className="xd-detail card">
      <div className="row between xd-section-head"><div><div className="micro">{copy.manage}</div><h2>{displayDomainName(domain)}</h2></div><button className="btn btn-sm btn-ghost" onClick={onClose}>{copy.close}</button></div>
      <div className="xd-domain-meta">
        <div><span>{copy.status}</span><strong>{domain.status || '—'}</strong></div>
        <div><span>{copy.expires}</span><strong>{formatExpiry(domain.expiresAt ?? domain.expires_at, locale)}</strong></div>
        <div><span>{copy.registrar}</span><strong>{domain.registrar || '—'}</strong></div>
      </div>
      <div className="xd-dnssec-card">
        <div><div className="row gap-2"><strong>{copy.dnssec}</strong><span className={`xd-pill ${dnssecState.tone}`}>{copy[dnssecState.label]}</span></div><p>{copy.dnssecHint}</p>{dnssec?.ds && <code>{dnssec.ds}</code>}</div>
        {dnssec && <button className="btn btn-sm btn-ghost" disabled={dnssecBusy || dnssecState.transitional} onClick={() => changeDnssec(!dnssecState.enabled)}>{dnssecBusy ? copy.dnssecBusy : dnssecState.enabled ? copy.dnssecDisable : copy.dnssecEnable}</button>}
      </div>
      <div className="row between xd-dns-title">
        <div className="row gap-2"><h3>{copy.dns}</h3><span className="mono">{records.length}</span></div>
        {!editor && <button className="btn btn-sm btn-primary" disabled={!capabilitiesReady} title={!capabilitiesReady ? copy.capabilitiesUnavailable : undefined} onClick={beginCreate}><XIcons.IconPlus size={12}/>{copy.addRecord}</button>}
      </div>
      {editor && (
        <form className="xd-dns-editor" onSubmit={saveRecord}>
          <div className="row between"><div className="micro">{editor.mode === 'edit' ? copy.editRecord : copy.createRecord}</div><button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditor(null)}><XIcons.IconX size={12}/>{copy.cancel}</button></div>
          <div className="xd-dns-form-grid">
            <label><span>{copy.type}</span><select required value={form.type} onChange={(event) => setType(event.target.value)}>{capabilities.record_types.map((type) => <option key={type} value={type}>{type}</option>)}</select></label>
            <label><span>{copy.name}</span><input required value={form.subdomain} onChange={(event) => setField('subdomain', event.target.value)} placeholder="@"/></label>
            <label className="xd-dns-value-field"><span>{srvRelevant ? copy.target : copy.value}</span><textarea required rows="3" value={form.value} onChange={(event) => setField('value', event.target.value)}/></label>
            <label><span>{copy.ttl}</span><input required type="number" value={form.ttl} min={capabilities.ttl.automatic ?? capabilities.ttl.min} max={capabilities.ttl.max} disabled={form.proxied && capabilities.proxied.requires_automatic_ttl} onChange={(event) => setField('ttl', event.target.value)}/><small>{ttlRange}{capabilities.ttl.automatic !== undefined ? ` · ${copy.ttlAutomatic}` : ''}</small></label>
            {priorityRelevant && <label><span>{copy.priority}</span><input required value={form.priority} onChange={(event) => setField('priority', event.target.value)} inputMode="numeric"/><small>{copy.priorityHint}</small></label>}
            {srvRelevant && <><label><span>{copy.weight}</span><input required value={form.weight} onChange={(event) => setField('weight', event.target.value)} inputMode="numeric"/></label><label><span>{copy.port}</span><input required value={form.port} onChange={(event) => setField('port', event.target.value)} inputMode="numeric"/></label></>}
            {caaRelevant && <><label><span>{copy.flags}</span><input required value={form.flags} onChange={(event) => setField('flags', event.target.value)} inputMode="numeric"/></label><label><span>{copy.tag}</span><input required list="xd-caa-tags" value={form.tag} onChange={(event) => setField('tag', event.target.value)}/><datalist id="xd-caa-tags"><option value="issue"/><option value="issuewild"/><option value="iodef"/></datalist></label></>}
            {proxyAvailable && <label><span>{copy.proxy}</span><button type="button" className={`xd-boolean ${form.proxied ? 'on' : ''}`} aria-pressed={form.proxied} onClick={() => setForm((current) => ({ ...current, proxied: !current.proxied, ttl: !current.proxied && capabilities.proxied.requires_automatic_ttl ? String(capabilities.ttl.automatic) : current.ttl }))}><span className="xd-toggle-label">{form.proxied ? copy.proxied : copy.dnsOnly}</span><span className="xd-toggle-track" aria-hidden="true"><span className="xd-toggle-knob"/></span></button><small>{proxyGroupWarning ? copy.proxyGroupHint : copy.proxyHint}</small></label>}
            {!proxyAvailable && proxyTypeSupported && <label><span>{copy.proxy}</span><span className="xd-proxy-state">{copy.proxyUnavailable}</span><small>{copy.proxyUnavailableHint}</small></label>}
            <label className="xd-dns-comment"><span>{copy.comment}</span><input maxLength="100" value={form.comment} onChange={(event) => setField('comment', event.target.value)}/><small>{copy.commentHint}</small></label>
            <label className="xd-dns-tags"><span>{copy.tags}</span><input value={form.tags} onChange={(event) => setField('tags', event.target.value)}/><small>{copy.tagsHint}</small></label>
          </div>
          <button className="btn btn-primary xd-dns-save" disabled={saving || !form.type.trim() || !form.subdomain.trim() || !form.value.trim()}>{saving ? copy.savingRecord : editor.mode === 'edit' ? copy.saveRecord : copy.createRecord}</button>
        </form>
      )}
      {notice && <div className="xd-notice xd-dns-notice"><XIcons.IconCheck size={13}/>{notice}</div>}
      {error && <div className="xd-error xd-dns-error">{error}</div>}
      {!loading && records.length > 0 && <div className="xd-dns-tools"><input aria-label={copy.filterDns} placeholder={copy.filterDns} value={recordQuery} onChange={(event) => { setRecordQuery(event.target.value); setRecordPage(0); }}/><select aria-label={copy.type} value={recordType} onChange={(event) => { setRecordType(event.target.value); setRecordPage(0); }}><option value="">{copy.allTypes}</option>{[...new Set(records.map((record) => String(record.type || '').toUpperCase()))].sort().map((type) => <option key={type} value={type}>{type}</option>)}</select><span className="mono">{copy.recordsShown.replace('{shown}', String(filteredRecordEntries.length)).replace('{total}', String(records.length))}</span></div>}
      {loading ? <div className="xd-inline-state">{copy.loadingDns}</div> : records.length === 0 ? <div className="xd-inline-state">{copy.noDns}</div> : filteredRecordEntries.length === 0 ? <div className="xd-inline-state">{copy.noDns}</div> : (
        <><div className="table-scroll">
          <table className="table xd-dns-table">
            <thead><tr><th>{copy.type}</th><th>{copy.name}</th><th>{copy.value}</th><th>{copy.ttl}</th><th>{copy.priority}</th>{capabilities.proxied.supported && <th>{copy.proxy}</th>}<th>{copy.actions}</th></tr></thead>
            <tbody>{visibleRecordEntries.map(({ record, index }) => {
              const readOnly = isDnsRecordReadOnly(record);
              const writableType = isDnsRecordTypeWritable(record, capabilities);
              const managed = isDnsRecordManaged(record);
              const shadowedCount = Array.isArray(record.shadowed_by) ? record.shadowed_by.length : 0;
              const recordCanProxy = dnsRecordSupportsProxy(record, record.type, capabilities);
              const recordTypeCanProxy = capabilities.proxied.record_types.includes(String(record.type || '').toUpperCase());
              return (
                <tr key={record.record_id || record.id || index}>
                  <td><span className="tag mono">{record.type}</span></td>
                  <td className="mono">{record.name ?? record.subdomain ?? '@'}</td>
                  <td className="mono xd-record-value">
                    <div>{record.value ?? record.content}</div>
                    {(readOnly || !writableType || managed || shadowedCount > 0 || record.comment || record.tags?.length) && <div className="xd-record-flags" title={record.comment || undefined}>
                      {readOnly && <span>{copy.recordLocked}</span>}
                      {!writableType && <span>{copy.unsupportedType}</span>}
                      {managed && <span>{copy.recordManaged}</span>}
                      {shadowedCount > 0 && <span>{copy.recordShadowed.replace('{count}', String(shadowedCount))}</span>}
                      {record.comment && <span>{record.comment}</span>}
                      {(record.tags || []).map((tag) => <span key={tag}>#{tag}</span>)}
                    </div>}
                  </td>
                  <td className="mono">{capabilities.ttl.automatic !== undefined && record.ttl === capabilities.ttl.automatic ? 'Auto' : record.ttl ?? '—'}</td>
                  <td className="mono">{record.priority ?? '—'}</td>
                  {capabilities.proxied.supported && <td><span className={`xd-proxy-state ${record.proxied ? 'on' : ''}`}>{recordTypeCanProxy && !recordCanProxy ? copy.proxyUnavailable : record.proxied === undefined ? '—' : record.proxied ? copy.proxied : copy.dnsOnly}</span></td>}
                  <td><div className="xd-dns-actions">{confirmIndex === index ? <><span>{copy.confirmDelete}</span><button className="btn btn-sm xd-delete-confirm" disabled={deletingIndex === index || readOnly} onClick={() => deleteRecord(record, index)}>{deletingIndex === index ? copy.deletingRecord : copy.deleteRecord}</button><button className="btn btn-sm btn-ghost" onClick={() => setConfirmIndex(null)}>{copy.cancel}</button></> : <><button className="btn btn-sm btn-ghost" disabled={readOnly || !writableType || !capabilitiesReady} title={readOnly ? copy.recordLocked : !writableType ? copy.unsupportedType : !capabilitiesReady ? copy.capabilitiesUnavailable : undefined} aria-label={`${copy.editRecord} ${record.name ?? record.subdomain ?? '@'}`} onClick={() => beginEdit(record, index)}><XIcons.IconEdit size={12}/></button><button className="btn btn-sm btn-ghost" disabled={readOnly || !capabilitiesReady} title={readOnly ? copy.recordLocked : !capabilitiesReady ? copy.capabilitiesUnavailable : undefined} aria-label={`${copy.deleteRecord} ${record.name ?? record.subdomain ?? '@'}`} onClick={() => setConfirmIndex(index)}><XIcons.IconTrash size={12}/></button></>}</div></td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>{recordPageCount > 1 && <div className="xd-pagination"><button className="btn btn-sm btn-ghost" disabled={safeRecordPage === 0} onClick={() => setRecordPage((page) => Math.max(0, page - 1))}>{copy.previous}</button><span className="mono">{safeRecordPage + 1} / {recordPageCount}</span><button className="btn btn-sm btn-ghost" disabled={safeRecordPage >= recordPageCount - 1} onClick={() => setRecordPage((page) => Math.min(recordPageCount - 1, page + 1))}>{copy.next}</button></div>}</>
      )}
    </div>
  );
};

const SchemaField = ({ name, property, required, value, onChange, copy }) => {
  const id = `xd-api-${name}`;
  const label = <span>{name}{required ? <b> *</b> : <em>{copy.optional}</em>}</span>;
  let input;
  if (Array.isArray(property?.enum)) {
    input = <select id={id} value={value ?? ''} onChange={(event) => onChange(event.target.value)}><option value="">—</option>{property.enum.map((option) => <option key={option} value={option}>{option}</option>)}</select>;
  } else if (property?.type === 'boolean') {
    input = (
      <select
        id={id}
        value={value === true ? 'true' : value === false ? 'false' : ''}
        onChange={(event) => onChange(event.target.value === '' ? '' : event.target.value === 'true')}
      >
        {!required && <option value="">—</option>}
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  } else if (property?.type === 'object' || property?.type === 'array') {
    input = <textarea id={id} className="mono" rows={Math.min(12, Math.max(5, String(value || '').split('\n').length + 1))} value={value ?? ''} onChange={(event) => onChange(event.target.value)}/>;
  } else {
    input = <input id={id} type={property?.type === 'number' || property?.type === 'integer' ? 'number' : 'text'} value={value ?? ''} min={property?.minimum} max={property?.maximum} placeholder={property?.example ?? ''} onChange={(event) => onChange(event.target.value)}/>;
  }
  return <label className="xd-schema-field" htmlFor={id}>{label}{input}{property?.description && <small>{property.description}</small>}</label>;
};

const ApiView = ({ copy, hasKey }) => {
  const [items, setItems] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState('');
  const [detail, setDetail] = React.useState(null);
  const [draft, setDraft] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState('');
  const [runError, setRunError] = React.useState('');
  const [running, setRunning] = React.useState(false);
  const [response, setResponse] = React.useState(null);

  React.useEffect(() => {
    let active = true; setLoading(true); setLoadError('');
    XApi.actionsService.list({ pageSize: 200, source: 'capability' })
      .then((all) => {
        if (!active) return;
        const next = all.filter(isDomainCapability);
        setItems(next);
        setSelectedId((current) => current || next[0]?.id || '');
      })
      .catch((error) => { if (active) setLoadError(error.message || copy.apiError); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    if (!selectedId) return;
    let active = true; setDetailLoading(true); setRunError(''); setResponse(null);
    XApi.actionsService.detail(selectedId)
      .then((next) => {
        if (!active) return;
        setDetail(next);
        setDraft(schemaDraft(next?.input || next?.inputSchema));
      })
      .catch((error) => { if (active) setRunError(error.message || copy.apiError); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [selectedId]);

  const schema = detail?.input || detail?.inputSchema || { type: 'object', properties: {} };
  const built = buildSchemaInput(schema, draft);
  const requestPreview = { action_id: selectedId, input: built.input || draft };
  const run = async () => {
    if (!hasKey) { setRunError(copy.apiKeyRequired); return; }
    if (built.error) { setRunError(built.error); return; }
    setRunning(true); setRunError(''); setResponse(null);
    try {
      const raw = await XApi.actionsService.execute({ actionId: selectedId, input: built.input });
      setResponse(raw);
      if (!raw.ok || raw.body?.success === false) setRunError(raw.body?.error?.message || `HTTP ${raw.status}`);
    } catch (error) { setRunError(error.message || 'Request failed'); }
    finally { setRunning(false); }
  };

  return (
    <section className="xd-api-view">
      <div className="xd-api-intro"><XIcons.IconCode size={18}/><p>{copy.apiLead}</p></div>
      {loading ? <div className="xd-empty card"><div className="spinner"/><p>{copy.apiLoading}</p></div>
        : loadError ? <div className="xd-error">{copy.apiError} {loadError}</div>
        : items.length === 0 ? <div className="xd-empty card"><p>{copy.apiEmpty}</p></div>
        : <div className="xd-api-layout">
          <aside className="card xd-api-nav">
            <div className="micro">xdomain actions</div>
            {items.map((item) => <button key={item.id} className={selectedId === item.id ? 'active' : ''} onClick={() => setSelectedId(item.id)}><span className="mono">{item.id}</span><small>{item.meta?.title || item.displayName}</small></button>)}
          </aside>
          <div className="xd-api-workbench">
            {detailLoading || !detail ? <div className="xd-empty card"><div className="spinner"/></div> : <>
              <header className="card xd-api-header">
                <div><div className="row gap-2"><span className="tag mono">{detail.id}</span><span className="xd-stable">{detail.status || 'stable'} · v{detail.version || '1'}</span></div><h2>{detail.meta?.title || detail.displayName || detail.id}</h2><p>{detail.meta?.description}</p></div>
                {detail.meta?.pricing?.billing_type && <div className="xd-billing"><span>billing</span><strong>{detail.meta.pricing.billing_type}</strong></div>}
              </header>
              <div className="xd-api-panes">
                <div className="card xd-request-pane">
                  <div className="row between xd-pane-head"><div className="micro">{copy.request}</div><span className="mono">POST /v1/actions/execute</span></div>
                  <div className="xd-schema-form">{Object.entries(schema.properties || {}).map(([name, property]) => <SchemaField key={name} name={name} property={property} required={(schema.required || []).includes(name)} value={draft[name]} copy={copy} onChange={(value) => setDraft((current) => ({ ...current, [name]: value }))}/>)}</div>
                  {runError && <div className="xd-error">{runError}</div>}
                  {!hasKey && <div className="xd-api-key-note"><XIcons.IconKey size={12}/>{copy.apiKeyRequired}</div>}
                  <button className="btn btn-primary xd-run" disabled={running || !hasKey} onClick={run}>{running ? <><div className="spinner"/>{copy.running}</> : <><XIcons.IconPlay size={12}/>{copy.run}</>}</button>
                </div>
                <div className="card xd-response-pane">
                  <div className="row between xd-pane-head"><div className="micro">{copy.response}</div>{response && <div className="row gap-2"><span className="tag mono">HTTP {response.status}</span><span className="tag mono">{response.latency}ms</span></div>}</div>
                  {response ? <pre className="code">{typeof response.body === 'string' ? response.body : JSON.stringify(response.body, null, 2)}</pre> : <div className="xd-response-empty"><XIcons.IconTerminal size={18}/><p>{copy.noResponse}</p></div>}
                </div>
              </div>
              <details className="card xd-schema-details"><summary><span>{copy.schema}</span><span className="mono">input + output</span></summary><div className="xd-schema-columns"><pre className="code">{JSON.stringify(schema, null, 2)}</pre><pre className="code">{JSON.stringify(detail.output || {}, null, 2)}</pre></div></details>
            </>}
          </div>
        </div>}
    </section>
  );
};

export const DomainsPage = () => {
  const { route, navigate } = XShell.useRoute();
  const { authed, loginMethod } = XShell.useAuth();
  const { locale } = useT();
  const copy = COPY[locale] || COPY.en;
  const env = XEnv.useEnv();
  const demo = route.params.demo === '1';
  const hasKey = !!env.find((entry) => entry.name === 'XAPI_KEY' && entry.value);
  const hasFullSession = authed && isFullAccountSession(loginMethod);
  const canManage = demo || hasFullSession;
  const view = route.params.view === 'api' ? 'api' : 'daily';
  const [domains, setDomains] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [offset, setOffset] = React.useState(0);
  const [hasNext, setHasNext] = React.useState(false);
  const ensureManagementKey = React.useCallback(async () => {
    if (demo) return undefined;
    if (!hasFullSession) throw new Error(copy.needsKey);
    const credential = await XApi.keyService.getDomainConsoleToken();
    if (!credential?.token) throw new Error('Could not create a domain management session');
    return credential.token;
  }, [copy.needsKey, demo, hasFullSession]);

  const runManagementAction = React.useCallback(async (actionId, input, signal) => {
    const managementKey = await ensureManagementKey();
    return action(actionId, input, signal, managementKey);
  }, [ensureManagementKey]);

  const loadDomains = React.useCallback(async (requestedOffset = 0) => {
    if (!canManage) return;
    setLoading(true); setLoadError('');
    try {
      const managementKey = await ensureManagementKey();
      const items = demo
        ? DEMO_DOMAINS.slice(requestedOffset, requestedOffset + DOMAIN_PAGE_SIZE)
        : normalizeDomainList(await action('domain.list', { limit: DOMAIN_PAGE_SIZE, offset: requestedOffset }, undefined, managementKey));
      setDomains(items.filter(isCloudflareDomain));
      setOffset(requestedOffset);
      setHasNext(items.length === DOMAIN_PAGE_SIZE);
    }
    catch (e) { setLoadError(e.message || 'Could not load domains'); }
    finally { setLoading(false); }
  }, [canManage, demo, ensureManagementKey]);
  React.useEffect(() => { loadDomains(); }, [loadDomains]);

  return (
    <div className="xd-page">
      <main className="xd-shell">
        <header className="xd-hero">
          <div><div className="micro xd-eyebrow"><span/><span/>{copy.eyebrow}</div><h1>{copy.title}</h1><p>{copy.subtitle}</p></div>
          <div className="xd-view-switch" role="group" aria-label="View">
            <button className={view === 'daily' ? 'active' : ''} onClick={() => navigate('domains', demo ? { demo: '1' } : {}) }><XIcons.IconGlobe size={13}/>{copy.daily}</button>
            <button className={view === 'api' ? 'active' : ''} onClick={() => navigate('domains', { view: 'api', ...(demo ? { demo: '1' } : {}) })}><XIcons.IconCode size={13}/>{copy.api}</button>
          </div>
        </header>

        {demo && <div className="xd-notice"><XIcons.IconGlobe size={14}/>{copy.demo}</div>}

        {view === 'api' ? <ApiView copy={copy} hasKey={hasKey}/> : (
          <>
            {!canManage && (
              <KeyGate copy={copy} authed={hasFullSession} navigate={navigate}/>
            )}
            {selected ? <DomainDetail demo={demo} domain={selected} copy={copy} locale={locale} runAction={runManagementAction} onClose={() => setSelected(null)}/>
              : <section className="xd-domains card">
                <div className="row between xd-section-head"><div><div className="micro">portfolio</div><h2>{copy.mine}</h2></div><button className="btn btn-sm btn-ghost" disabled={!canManage || loading} onClick={() => loadDomains(offset)}><XIcons.IconRefresh size={12}/>{copy.refresh}</button></div>
                {loading ? <div className="xd-empty"><div className="spinner"/><p>{copy.loading}</p></div> : loadError ? <div className="xd-error">{loadError}</div> : domains.length === 0 ? <div className="xd-empty"><div className="xd-empty-globe"><XIcons.IconGlobe size={22}/></div><h3>{copy.noDomains}</h3><p>{copy.noDomainsHint}</p></div> : (
                  <><div className="xd-domain-list">{domains.map((domain) => <button key={domain.id} onClick={() => setSelected(domain)}><div className="xd-domain-mark"><XIcons.IconGlobe size={16}/></div><div className="grow"><strong>{displayDomainName(domain)}</strong><span>{domain.registrar || 'xdomain'}</span></div><span className={`xd-pill ${statusTone(domain.status)}`}>{domain.status || 'pending'}</span><span className="xd-expiry">{formatExpiry(domain.expiresAt ?? domain.expires_at, locale)}</span><XIcons.IconChevronRight size={14}/></button>)}</div>{(offset > 0 || hasNext) && <div className="xd-pagination"><button className="btn btn-sm btn-ghost" disabled={loading || offset === 0} onClick={() => loadDomains(Math.max(0, offset - DOMAIN_PAGE_SIZE))}>{copy.previous}</button><span className="mono">{Math.floor(offset / DOMAIN_PAGE_SIZE) + 1}</span><button className="btn btn-sm btn-ghost" disabled={loading || !hasNext} onClick={() => loadDomains(offset + DOMAIN_PAGE_SIZE)}>{copy.next}</button></div>}</>
                )}
              </section>}
          </>
        )}
      </main>
      <Footer/>
    </div>
  );
};
